import React, { ComponentType } from "react";
import { createRoot, Root } from "react-dom/client";
import { v4 as uuid } from "uuid";

import { ModalServiceEntry } from "./modal-service-entry.component";
import { ModalContainerComponentProps } from "../../components/cp-modal/modal-container.component";

type ModalServiceProps<T = Record<string, unknown>> = T & {
  onSubmit: (data: unknown) => void;
};

type ModalServicePromise<T = unknown> = Promise<T> & {
  __modalServicePromiseResolveId?: string;
};

class ModalService {
  promiseResolveMap: Record<string, (data: unknown) => void> = {};
  rootMap: Record<string, Root> = {};
  modalsOpen = 0;
  hideScrollbarClassName = "--modalService-hide-scroll-bar";

  render = <T extends Record<string, unknown> = Record<string, unknown>>(
    component: ComponentType<ModalServiceProps<T>>,
    props?: Omit<T, "onSubmit">,
    modalProps?: ModalContainerComponentProps & { overlay?: boolean },
  ): ModalServicePromise<unknown> => {
    const id = `__modalService_${uuid()}`;
    let onSubmit: (modalId: string, data: unknown) => void;

    const promise: ModalServicePromise<unknown> = new Promise((resolve) => {
      this.promiseResolveMap[id] = resolve;
      onSubmit = (modalId: string, data: unknown) => {
        resolve(data);
        this.destroy(modalId);
      };
    });

    const div = document.createElement("div");
    div.id = id;
    document.body.appendChild(div);
    const root = createRoot(div);
    this.rootMap[id] = root;

    root.render(
      <ModalServiceEntry
        id={id}
        component={component}
        props={props}
        modalProps={modalProps}
        onSubmit={onSubmit!}
      />,
    );

    // add id to promise so we can use it if they pass it back to destroy it
    promise.__modalServicePromiseResolveId = id;
    return promise;
  };

  destroy = (modalPromise?: ModalServicePromise | string): void => {
    let id: string | undefined;

    // destroy single modal from promise
    if (
      modalPromise &&
      typeof modalPromise === "object" &&
      "__modalServicePromiseResolveId" in modalPromise
    ) {
      id = modalPromise.__modalServicePromiseResolveId;
      // destroy single modal from id
    } else if (typeof modalPromise === "string") {
      id = modalPromise;
    }

    if (!id) return;
    const resolve = this.promiseResolveMap[id];
    const root = this.rootMap[id];
    if (root) {
      root.unmount();
      delete this.rootMap[id];
    }
    const div = document.querySelector(`#${id}`);
    if (div) div.remove();
    resolve?.(false);
    delete this.promiseResolveMap[id];
  };

  // keep track of modals opened/closed (regular modals + modals generated by the modal service)
  // if any modal is open, remove body scrolling
  __openModal = (hideScroll = true) => {
    modalService.modalsOpen++;
    if (modalService.modalsOpen === 1 && hideScroll) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.classList.add(this.hideScrollbarClassName);
      document.body.style.marginRight = `${scrollbarWidth / 10}rem`;
    }
  };
  __closeModal = () => {
    modalService.modalsOpen--;
    if (modalService.modalsOpen === 0) {
      document.body.classList.remove(this.hideScrollbarClassName);
      document.body.style.marginRight = "initial";
    }
  };
}

export const modalService = new ModalService();
